import { utcNow } from "@dashboard-config"
import {
  CALENDAR_EVENTS_PAGE_SIZE,
  type CalendarEventsPageInput,
  type CalendarEventsResponse,
} from "@site-types/calendar"

const PROD_EVENTS_ENDPOINT = "https://events-api.playbrain.io/api/"
const FALLBACK_STAGE_EVENTS_ENDPOINTS: Record<string, string> = {
  dev: "https://events-api.dev.playbrain.io/api/",
  staging: "https://events-api.staging.playbrain.io/api/",
  prod: PROD_EVENTS_ENDPOINT,
  production: PROD_EVENTS_ENDPOINT,
}
const START_DATETIME = "1990-01-01T00:00:00.000Z"
export const CALENDAR_FETCH_TIMEOUT_MS = 3000
export { CALENDAR_EVENTS_PAGE_SIZE }

const fallbackCalendarEvents: CalendarEventsResponse = {
  totalCount: 0,
  results: [],
}

type CalendarEndpointEnv = Pick<
  ImportMetaEnv,
  | "PUBLIC_EVENTS_ENDPOINT"
  | "PUBLIC_STAGE"
  | "PUBLIC_DEV_EVENTS_ENDPOINT"
  | "PUBLIC_STAGING_EVENTS_ENDPOINT"
  | "PUBLIC_PROD_EVENTS_ENDPOINT"
  | "VITE_PUBLIC_EVENTS_ENDPOINT"
  | "VITE_PUBLIC_STAGE"
  | "VITE_PUBLIC_DEV_EVENTS_ENDPOINT"
  | "VITE_PUBLIC_STAGING_EVENTS_ENDPOINT"
  | "VITE_PUBLIC_PROD_EVENTS_ENDPOINT"
>

function runtimeEnvValue(
  env: CalendarEndpointEnv,
  key: keyof CalendarEndpointEnv,
  includeProcessEnv: boolean
): string | undefined {
  return env[key] || (includeProcessEnv && typeof process !== "undefined" ? process.env[key] : undefined)
}

function calendarRuntimeStage(env: CalendarEndpointEnv, includeProcessEnv: boolean) {
  return (
    runtimeEnvValue(env, "PUBLIC_STAGE", includeProcessEnv) ||
    runtimeEnvValue(env, "VITE_PUBLIC_STAGE", includeProcessEnv) ||
    ""
  )
}

function configuredCalendarEventsEndpoint(
  env: CalendarEndpointEnv,
  includeProcessEnv: boolean
): string | undefined {
  return (
    runtimeEnvValue(env, "PUBLIC_EVENTS_ENDPOINT", includeProcessEnv) ||
    runtimeEnvValue(env, "VITE_PUBLIC_EVENTS_ENDPOINT", includeProcessEnv)
  )
}

export function shouldUseCalendarMockFallback(
  stage = import.meta.env.PUBLIC_STAGE || import.meta.env.VITE_PUBLIC_STAGE
): boolean {
  void stage
  return false
}

export function calendarFallbackEvents(
  stage = import.meta.env.PUBLIC_STAGE || import.meta.env.VITE_PUBLIC_STAGE
): CalendarEventsResponse {
  void stage
  return fallbackCalendarEvents
}

function calendarEndDateTime() {
  return utcNow().add(2, "year").endOf("day").toISOString()
}

function configuredStageEventsEndpoint(
  stage: string,
  env: CalendarEndpointEnv,
  includeProcessEnv: boolean
): string | undefined {
  if (stage === "dev")
    return (
      runtimeEnvValue(env, "PUBLIC_DEV_EVENTS_ENDPOINT", includeProcessEnv) ||
      runtimeEnvValue(env, "VITE_PUBLIC_DEV_EVENTS_ENDPOINT", includeProcessEnv)
    )
  if (stage === "staging")
    return (
      runtimeEnvValue(env, "PUBLIC_STAGING_EVENTS_ENDPOINT", includeProcessEnv) ||
      runtimeEnvValue(env, "VITE_PUBLIC_STAGING_EVENTS_ENDPOINT", includeProcessEnv)
    )
  if (stage === "prod" || stage === "production")
    return (
      runtimeEnvValue(env, "PUBLIC_PROD_EVENTS_ENDPOINT", includeProcessEnv) ||
      runtimeEnvValue(env, "VITE_PUBLIC_PROD_EVENTS_ENDPOINT", includeProcessEnv)
    )
  return undefined
}

export function calendarEventsEndpointCandidates(
  stage?: string,
  configuredEndpoint?: string,
  endpointEnv?: CalendarEndpointEnv
): string[] {
  const env = endpointEnv ?? import.meta.env
  const includeProcessEnv = endpointEnv === undefined
  const normalizedStage = stage || calendarRuntimeStage(env, includeProcessEnv)
  const productionEndpoint =
    configuredStageEventsEndpoint("prod", env, includeProcessEnv)?.trim() || PROD_EVENTS_ENDPOINT
  const explicitEndpoint = configuredEndpoint?.trim()
  if (explicitEndpoint) return Array.from(new Set([explicitEndpoint, productionEndpoint]))

  const stageEndpoint =
    configuredStageEventsEndpoint(normalizedStage, env, includeProcessEnv)?.trim() ||
    FALLBACK_STAGE_EVENTS_ENDPOINTS[normalizedStage]
  const endpoint =
    stageEndpoint || configuredCalendarEventsEndpoint(env, includeProcessEnv)?.trim() || productionEndpoint
  return Array.from(new Set([endpoint, productionEndpoint]))
}

function graphEventsUrl(base: string) {
  return new URL("graph", base.endsWith("/") ? base : `${base}/`)
}

function restrictedEventsApiSecret(endpoint: string) {
  const hostname = new URL(endpoint).hostname
  if (
    hostname !== "events-api.dev.playbrain.io" &&
    hostname !== "events-api.staging.playbrain.io" &&
    hostname !== "dev.events-api.playbrain.io" &&
    hostname !== "staging.events-api.playbrain.io"
  ) {
    return undefined
  }

  return typeof process !== "undefined"
    ? process.env.EVENTS_API_WAF_BYPASS_SECRET?.trim() || undefined
    : undefined
}

function eventsApiGraphqlHeaders(endpoint: string): Record<string, string> {
  const headers = { "content-type": "application/json" }
  const secret = restrictedEventsApiSecret(endpoint)
  if (!secret) return headers

  return {
    ...headers,
    "x-events-api-secret": secret,
  }
}

type EventsGraphqlGame = {
  game?: {
    primaryImage?: string | null
  } | null
}

type EventsGraphqlEvent = {
  id?: string | null
  imageUrl?: string | null
  name?: string | null
  games?: EventsGraphqlGame[] | null
}

type EventsGraphqlPayload = {
  data?: {
    allEvents?: EventsGraphqlEvent[] | null
  } | null
  errors?: unknown[] | null
}

function isEventsGraphqlPayload(value: unknown): value is EventsGraphqlPayload {
  if (!value || typeof value !== "object") return false
  const candidate = value as EventsGraphqlPayload
  return !candidate.errors?.length && Array.isArray(candidate.data?.allEvents)
}

function normalizeCalendarPageInput(input: CalendarEventsPageInput = {}) {
  const offset = Number.isInteger(input.offset) && Number(input.offset) > 0 ? Number(input.offset) : 0
  return { limit: CALENDAR_EVENTS_PAGE_SIZE, offset }
}

function adaptGraphqlEvents(
  events: EventsGraphqlEvent[],
  { limit, offset }: { limit: number; offset: number }
): CalendarEventsResponse {
  const results = events
    .filter((event): event is EventsGraphqlEvent & { id: string; name: string } => {
      return typeof event.id === "string" && typeof event.name === "string"
    })
    .map((event) => ({
      id: event.id,
      imageUrl: typeof event.imageUrl === "string" ? event.imageUrl : undefined,
      name: event.name,
      games: (event.games ?? [])
        .map((entry) => entry.game?.primaryImage)
        .filter((primaryImage): primaryImage is string => typeof primaryImage === "string")
        .map((primaryImage) => ({ primaryImage })),
    }))

  const hasMore = results.length === limit

  return {
    hasMore,
    nextOffset: hasMore ? offset + results.length : undefined,
    pageSize: limit,
    totalCount: results.length,
    results,
  }
}

const CALENDAR_EVENTS_QUERY = `
  query CalendarEvents(
    $activeFrom: DateTime
    $activeTo: DateTime
    $limit: Int!
    $offset: Int
  ) {
    allEvents(
      activeFrom: $activeFrom
      activeTo: $activeTo
      company: { producerId: "uwSyRwdDwu", organizerId: "1", match: ANY }
      limit: $limit
      offset: $offset
      sortBy: START_DATETIME_DESC
    ) {
      id
      imageUrl
      name
      games {
        game {
          primaryImage
        }
      }
    }
  }
`

async function fetchGraphqlCalendarEvents(
  endpoint: string,
  fetchImpl: typeof fetch,
  signal: AbortSignal,
  pageInput: CalendarEventsPageInput = {}
): Promise<CalendarEventsResponse | null> {
  const activeTo = calendarEndDateTime()
  const page = normalizeCalendarPageInput(pageInput)

  const response = await fetchImpl(graphEventsUrl(endpoint), {
    body: JSON.stringify({
      query: CALENDAR_EVENTS_QUERY,
      variables: {
        activeFrom: START_DATETIME,
        activeTo,
        limit: page.limit,
        offset: page.offset,
      },
    }),
    headers: eventsApiGraphqlHeaders(endpoint),
    method: "POST",
    signal,
  })

  if (!response.ok) {
    console.error(`Calendar events GraphQL request to ${endpoint} failed with status ${response.status}`)
    return null
  }

  const payload: unknown = await response.json()
  if (!isEventsGraphqlPayload(payload)) {
    console.error(`Calendar events GraphQL response from ${endpoint} did not match the expected shape`)
    return null
  }

  return adaptGraphqlEvents(payload.data?.allEvents ?? [], page)
}

export async function getCalendarEventsPage(
  pageInput: CalendarEventsPageInput = {},
  stage?: string,
  fetchImpl: typeof fetch = fetch,
  configuredEndpoint?: string,
  endpointEnv?: CalendarEndpointEnv
): Promise<CalendarEventsResponse> {
  for (const endpoint of calendarEventsEndpointCandidates(stage, configuredEndpoint, endpointEnv)) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), CALENDAR_FETCH_TIMEOUT_MS)
    try {
      const events = await fetchGraphqlCalendarEvents(endpoint, fetchImpl, controller.signal, pageInput)

      if (!events) continue
      if (events.totalCount > 0 || (pageInput.offset ?? 0) > 0) return events
      console.error(`Calendar events response from ${endpoint} did not include any records`)
    } catch (error) {
      console.error(`Calendar events request to ${endpoint} failed`, error)
    } finally {
      clearTimeout(timeout)
    }
  }

  return calendarFallbackEvents(stage)
}

export async function getCalendarEvents(
  stage?: string,
  fetchImpl: typeof fetch = fetch,
  configuredEndpoint?: string,
  endpointEnv?: CalendarEndpointEnv
): Promise<CalendarEventsResponse> {
  return getCalendarEventsPage({}, stage, fetchImpl, configuredEndpoint, endpointEnv)
}
